import api from './api.service';

export interface WorkOrderItem {
  lineItemId: string;
  quantity: number;
  itemCode: string;
  itemDescription: string;
  uom: string;
}

export interface WorkOrderDocument {
  documentType: string;
  documentName: string;
  filePath: string;
  fileSize?: number;
  mimeType?: string;
  notes?: string;
}

/** FG receipt rows for a work order (from job schedule completion). */
export interface ProductionReceiptRow {
  id: string;
  receipt_number: string;
  receipt_date: string;
  produced_quantity: number;
  accepted_quantity: number;
  rejected_quantity: number;
  uom?: string | null;
  warehouse_id: string;
  receipt_status: string;
  is_posted: boolean;
  job_schedule_id?: string | null;
  item?: { item_code?: string; item_name?: string };
  jobSchedule?: { schedule_number?: string };
}

export const WORK_ORDER_EXECUTION_SCOPES = [
  'Manufacturing',
  'Fabrication',
  'Repair',
  'Repair + Manufacturing',
  'Outsource',
] as const;

export const WORK_ORDER_DEVIATION_TYPES = ['RFD', 'Email', 'N/A'] as const;

export type WorkOrderExecutionScope = (typeof WORK_ORDER_EXECUTION_SCOPES)[number];
export type WorkOrderDeviationType = (typeof WORK_ORDER_DEVIATION_TYPES)[number];

export interface MaterialUtilizationLineInput {
  workOrderLineItemId?: string | null;
  drawingLineNo: string;
  clientMaterialSpecification?: string;
  materialGrade?: string;
  /** Heat number (API/DB: headNo / head_no). */
  headNo?: string;
  verified: boolean;
  notVerified: boolean;
  remarks?: string;
}

export interface WorkOrderGroup {
  items: WorkOrderItem[];
  bomId?: string;
  targetWarehouseId: string;
  plannedStartDate: Date | string;
  plannedEndDate: Date | string;
  priority?: string;
  notes?: string;
  workScope?: string[];
  documents?: WorkOrderDocument[];
  executionScope: WorkOrderExecutionScope;
  deviationType: WorkOrderDeviationType;
  clientScopeOfWork?: string | null;
  clientQualityRequirements?: string | null;
  productionSupervisorUserId: string;
  qualityEngineerUserId: string;
  materialUtilizationLines?: MaterialUtilizationLineInput[];
}

export interface ConversionData {
  groups: WorkOrderGroup[];
}

export interface WorkOrderLineItem {
  id: string;
  work_order_id: string;
  sales_order_id: string;
  sales_order_line_item_id: string;
  /** Set when converting from SO with a resolved BOQ chain; used for BOQ line document visibility. */
  boq_line_item_id?: string | null;
  line_number: number;
  item_code?: string;
  item_description: string;
  /** Optional display name from joined item master (API may include). */
  item_name?: string | null;
  drawing_reference_number?: string;
  part_number?: string;
  part_description?: string;
  heat_number?: string;
  revision?: string | null;
  planned_quantity: number;
  produced_quantity: number;
  uom: string;
  notes?: string;
}

export interface WorkOrderDocument {
  id: string;
  work_order_id: string;
  document_type: string;
  document_name: string;
  file_url: string;
  uploaded_by?: string;
  uploaded_at?: Date;
}

export interface WorkOrderMaterialUtilizationLine {
  id: string;
  work_order_id: string;
  work_order_line_item_id?: string | null;
  line_number: number;
  drawing_line_no?: string;
  client_material_specification?: string;
  material_grade?: string;
  /** Heat number */
  head_no?: string;
  verified: boolean;
  not_verified: boolean;
  remarks?: string;
}

export interface WorkOrderBoqStageSummary {
  id: string;
  status: string;
  sequence_number: number;
  stage_code: string;
  stage_name: string;
  started_date?: string | null;
  completed_date?: string | null;
  work_order_line_item_id?: string | null;
  outsourced_pr_id?: string | null;
  outsourced_trace_status?: string | null;
  /** Shop-floor workstation for productivity / utilization; set while stage is Pending. */
  workstation_id?: string | null;
  workstation_name?: string | null;
  /** Operator = assigned user. */
  assigned_to_user_id?: string | null;
  operator_name?: string | null;
  operator_email?: string | null;
  inspector_user_id?: string | null;
  inspector_name?: string | null;
  inspector_email?: string | null;
  verifier_user_id?: string | null;
  verifier_name?: string | null;
  verifier_email?: string | null;
  production_unit_number?: number | null;
  /** Estimated runtime hours for this stage (setup + run×qty from BOQ). */
  estimated_hours?: number;
  /** Resolved actual hours (stored or start→end span). */
  actual_hours?: number;
  /** Company policy: all operations require stage completion inspection before complete. */
  inspection_required?: boolean;
  inspection_status?: string | null;
  /** Accumulated stage notes / work log (incl. EOD lines). */
  notes?: string | null;
}

export interface WorkOrderBoqOperationRow {
  id: string;
  boq_line_item_id: string;
  operation_id?: string;
  description: string;
  sequence_number: number;
  setup_time_hours: number;
  run_time_per_unit_hours: number;
  /** Total estimated runtime for this operation on the WO line (setup + run×planned qty). */
  estimated_runtime_hours?: number;
  /** Sum of actual hours across linked work order stages for this operation. */
  actual_hours?: number;
  quantity?: number;
  labor_rate_per_hour: number;
  overhead_rate_per_hour: number;
  machine_rate_per_hour: number;
  tooling_cost: number;
  total_cost: number;
  is_outsourced: boolean;
  outsource_cost?: number;
  /** WO line planned qty — default for outsourced PR quantity */
  work_order_planned_quantity?: number;
  /** WO line UOM — default for outsourced PR */
  work_order_uom?: string;
  notes?: string;
  outsourced_scope_detail?: string | null;
  outsourced_supplier_category_ids?: string[];
  /** One row per production unit for this BOQ operation */
  work_order_stages: WorkOrderBoqStageSummary[];
  /** @deprecated use work_order_stages[0] */
  work_order_stage: WorkOrderBoqStageSummary | null;
}

export interface WorkOrderBoqLinePayload {
  work_order_line_item_id: string;
  line_number: number;
  sales_order_line_item_id: string;
  item_code?: string;
  item_description: string;
  revision?: string | null;
  boq_line_item_id: string | null;
  operations: WorkOrderBoqOperationRow[];
}

export interface WorkOrderBoqOperationsPayload {
  has_boq_chain: boolean;
  boq_id: string | null;
  quotation_id?: string | null;
  execution_scope?: string | null;
  total_estimated_hours?: number;
  total_actual_hours?: number;
  lines: WorkOrderBoqLinePayload[];
}

export interface WorkOrderBoqLineDocumentAttachment {
  id: string;
  document_name: string;
  file_name: string;
  file_size_bytes: number;
  mime_type?: string;
  document_type?: string;
  document_category?: string;
  uploaded_by?: string;
  created_at: string;
}

export interface WorkOrderBoqLineDocumentsLine {
  work_order_line_item_id: string;
  line_number: number;
  boq_line_item_id: string | null;
  boq_line_source: 'persisted' | 'resolved_from_quotation' | null;
  attachments: WorkOrderBoqLineDocumentAttachment[];
}

export interface WorkOrderBoqLineDocumentsPayload {
  lines: WorkOrderBoqLineDocumentsLine[];
}

/** NM_WP_72–73: tool / asset issue, return, damage on a work order */
export interface WorkOrderAssetMovementRow {
  id: string;
  movement_type: 'issue' | 'return' | 'damage';
  status: string;
  work_order_id?: string;
  work_order_stage_id?: string | null;
  asset_id?: string | null;
  asset_code?: string | null;
  asset_name?: string | null;
  issued_to_employee_id?: string | null;
  asset_description: string;
  source?: 'asset' | 'legacy';
  quantity: number;
  uom?: string | null;
  notes?: string | null;
  created_at?: string;
}

export interface InlandDeviationPrefillResponse {
  work_order_id: string;
  work_order_stage_id: string;
  boq_line_item_operation_id: string;
  boq_snapshot: Record<string, unknown>;
  default_pr_lines: {
    work_order_planned_quantity?: number;
    work_order_uom?: string;
    outsource_cost_default?: number;
  };
}

/** WO line mapped to a BOQ line — prefill BOM name / qty / UOM on Create BOM. */
export interface BoqBomLineOption {
  work_order_line_item_id: string;
  boq_line_item_id: string;
  work_order_line_number: number;
  sales_order_line_number: number;
  bom_name: string;
  planned_quantity: number;
  uom: string;
  operations?: Array<{
    operation_id: string;
    description: string;
    sequence_number: number;
    setup_time_hours: number;
    run_time_per_unit_hours: number;
    labor_rate_per_hour: number;
    overhead_rate_per_hour: number;
    machine_rate_per_hour: number;
    tooling_cost: number;
    is_outsourced: boolean;
    outsource_cost: number | null;
    outsourced_scope_detail: string | null;
    outsourced_supplier_category_ids: string[] | null;
    notes: string | null;
  }>;
}

/** BOM picker for Create Material Request → From BOM. */
export interface MaterialRequestBomOptionRow {
  id: string;
  bom_code: string;
  bom_name: string;
  item_id: string;
  item_name: string;
  is_linked_on_work_order: boolean;
  is_default: boolean;
}

export interface MaterialRequestBomOptionsPayload {
  work_order: { id: string; planned_quantity: number; item_id: string | null };
  boms: MaterialRequestBomOptionRow[];
}

export interface WorkOrder {
  id: string;
  work_order_number: string;
  company_id: string;
  item_id: string;
  item_code?: string;
  item_name?: string;
  customer_name?: string | null;
  bom_id?: string;
  order_type: string;
  sales_order_id?: string;
  revenue_service_type_id?: string | null;
  sales_order_number?: string;
  source_document_type?: string | null;
  source_document_id?: string | null;
  production_request_number?: string | null;
  project_id?: string;
  project_name?: string;
  project?: { id?: string; project_name?: string };
  planned_quantity: number;
  produced_quantity: number;
  uom: string;
  order_date: Date;
  planned_start_date?: Date;
  planned_end_date?: Date;
  actual_start_date?: Date;
  actual_end_date?: Date;
  target_warehouse_id: string;
  priority: string;
  work_order_status: string;
  approval_status: string;
  notes?: string;
  execution_scope?: string;
  deviation_type?: string;
  client_scope_of_work?: string;
  client_quality_requirements?: string;
  /** NM_WP_70: WO-only notes; can diverge from SO customer_requirements_text */
  wo_customer_requirements_notes?: string | null;
  production_supervisor_user_id?: string;
  next_action_user_id?: string | null;
  quality_engineer_user_id?: string;
  production_supervisor?: {
    id: string;
    first_name: string;
    last_name?: string;
    email: string;
  } | null;
  quality_engineer?: {
    id: string;
    first_name: string;
    last_name?: string;
    email: string;
  } | null;
  materialUtilizationLines?: WorkOrderMaterialUtilizationLine[];
  /** Sum of stage estimated hours on this work order. */
  total_estimated_hours?: number;
  /** Sum of resolved stage actual hours on this work order. */
  total_actual_hours?: number;
  created_by: string;
  created_at: Date;
  updated_at: Date;
  lineItems?: WorkOrderLineItem[];
  documents?: WorkOrderDocument[];
}

class WorkOrderService {
  /**
   * Create work orders from a sales order
   */
  async convertFromSalesOrder(
    salesOrderId: string,
    data: ConversionData
  ): Promise<WorkOrder[]> {
    const response = await api.post(
      `/manufacturing/work-orders/convert-from-sales-order/${salesOrderId}`,
      data
    );
    console.log('Convert SO to WO - Full response:', response);
    // api.post returns { success, message, data }; work orders array is in data
    const workOrders = (response as { data?: WorkOrder[] })?.data;
    return Array.isArray(workOrders) ? workOrders : [];
  }

  /**
   * Create work orders from a production request (same payload shape as sales order conversion).
   */
  async convertFromProductionRequest(
    productionRequestId: string,
    data: ConversionData
  ): Promise<WorkOrder[]> {
    const response = await api.post(
      `/manufacturing/work-orders/convert-from-production-request/${productionRequestId}`,
      data
    );
    const workOrders = (response as { data?: WorkOrder[] })?.data;
    return Array.isArray(workOrders) ? workOrders : [];
  }

  /**
   * Get all work orders
   */
  async getAll(filters?: any): Promise<WorkOrder[]> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, String(value));
      });
    }
    const response = await api.get(`/manufacturing/work-orders?${params.toString()}`);
    console.log('Work Orders API response:', response);
    console.log('Work Orders data:', response.data);
    return response.data || [];
  }

  /**
   * Get a single work order by ID
   */
  async getById(id: string): Promise<WorkOrder> {
    const response = await api.get(`/manufacturing/work-orders/${id}`);
    console.log('Get Work Order by ID - Full response:', response);
    console.log('Get Work Order by ID - response.data:', response.data);
    return response.data;
  }

  /** Finished goods receipts posted for this work order (job completions → inventory). */
  async getProductionReceipts(workOrderId: string): Promise<ProductionReceiptRow[]> {
    const response = await api.get<ProductionReceiptRow[]>(
      `/manufacturing/work-orders/${workOrderId}/production-receipts`
    );
    return response.data ?? [];
  }

  /**
   * Delete a work order document
   */
  async deleteDocument(workOrderId: string, documentId: string): Promise<void> {
    const response = await api.delete(`/manufacturing/work-orders/${workOrderId}/documents/${documentId}`);
    return response.data;
  }

  /** NM_WP_67: Pull SO documents not yet linked on this WO. */
  async syncSalesOrderDocuments(workOrderId: string): Promise<{ copied: number }> {
    const res = await api.post<{ copied: number }>(
      `/manufacturing/work-orders/${workOrderId}/sync-sales-order-documents`,
      {}
    );
    const n = Number(res?.data?.copied);
    return { copied: Number.isFinite(n) ? n : 0 };
  }

  async getAssetMovements(workOrderId: string): Promise<WorkOrderAssetMovementRow[]> {
    const res = await api.get<WorkOrderAssetMovementRow[]>(
      `/manufacturing/work-orders/${workOrderId}/asset-movements`
    );
    const rows = res?.data;
    return Array.isArray(rows) ? rows : [];
  }

  async createAssetMovement(
    workOrderId: string,
    body: {
      movement_type: 'issue' | 'return' | 'damage';
      asset_id: string;
      to_employee_id?: string | null;
      work_order_stage_id?: string | null;
      notes?: string | null;
    }
  ): Promise<WorkOrderAssetMovementRow> {
    const res = await api.post<WorkOrderAssetMovementRow>(
      `/manufacturing/work-orders/${workOrderId}/asset-movements`,
      body
    );
    const row = res?.data;
    if (!row) throw new Error(res?.message || 'Failed to record asset movement');
    return row;
  }

  /**
   * Create a new work order
   */
  async create(data: Partial<WorkOrder>): Promise<WorkOrder> {
    const response = await api.post('/manufacturing/work-orders', data);
    return response.data;
  }

  /**
   * Update an existing work order (supports snake_case WO fields and camelCase execution payload)
   */
  async update(
    id: string,
    data: Omit<Partial<WorkOrder>, 'materialUtilizationLines'> & {
      executionScope?: WorkOrderExecutionScope;
      deviationType?: WorkOrderDeviationType;
      clientScopeOfWork?: string | null;
      clientQualityRequirements?: string | null;
      wo_customer_requirements_notes?: string | null;
      productionSupervisorUserId?: string | null;
      qualityEngineerUserId?: string | null;
      materialUtilizationLines?: MaterialUtilizationLineInput[];
    }
  ): Promise<WorkOrder> {
    const response = await api.put(`/manufacturing/work-orders/${id}`, data);
    return response.data;
  }

  /**
   * Release a work order
   */
  async release(id: string): Promise<WorkOrder> {
    const response = await api.post(`/manufacturing/work-orders/${id}/release`);
    return response.data;
  }

  /**
   * Start a work order
   */
  async start(id: string): Promise<WorkOrder> {
    const response = await api.post(`/manufacturing/work-orders/${id}/start`);
    return response.data;
  }

  /**
   * Complete a work order
   */
  async complete(id: string): Promise<WorkOrder> {
    const response = await api.post(`/manufacturing/work-orders/${id}/complete`);
    return response.data;
  }

  /**
   * Release a work order after inspection
   */
  async releaseAfterInspection(id: string): Promise<WorkOrder> {
    const response = await api.post(`/manufacturing/work-orders/${id}/release-after-inspection`);
    return response.data;
  }

  /**
   * Get inspections for a work order
   */
  async getInspections(id: string): Promise<any[]> {
    const response = await api.get(`/manufacturing/work-orders/${id}/inspections`);
    return response.data || [];
  }

  /**
   * BOQ line item operations per WO line, merged with work order stages for shop-floor status.
   */
  async getBoqOperations(id: string): Promise<WorkOrderBoqOperationsPayload> {
    const response = await api.get<WorkOrderBoqOperationsPayload>(
      `/manufacturing/work-orders/${id}/boq-operations`
    );
    if (response?.data) return response.data;
    throw new Error(response?.message || 'Failed to load BOQ operations');
  }

  /** BOQ line attachments per WO line (`Attachment` + `Technical` from estimation). */
  async getBoqLineDocuments(workOrderId: string): Promise<WorkOrderBoqLineDocumentsPayload> {
    const response = await api.get<WorkOrderBoqLineDocumentsPayload>(
      `/manufacturing/work-orders/${workOrderId}/boq-line-documents`
    );
    const payload = response?.data;
    if (payload?.lines) return payload;
    throw new Error(response?.message || 'Failed to load BOQ line documents');
  }

  /** BOQ lines linked to this work order (via SO → quotation → BOQ). */
  async getBoqBomLineOptions(workOrderId: string): Promise<BoqBomLineOption[]> {
    const response = await api.get(
      `/manufacturing/work-orders/${workOrderId}/boq-bom-line-options`
    );
    const data = (response as { data?: BoqBomLineOption[] }).data;
    return Array.isArray(data) ? data : [];
  }

  async getMaterialRequestBomOptions(workOrderId: string): Promise<MaterialRequestBomOptionsPayload> {
    const response = await api.get(
      `/manufacturing/work-orders/${workOrderId}/material-request-bom-options`
    );
    const r = response as { data?: MaterialRequestBomOptionsPayload; message?: string };
    if (r?.data?.work_order && Array.isArray(r.data.boms)) {
      return r.data;
    }
    throw new Error(r?.message || 'Failed to load BOM options for this work order');
  }

  /**
   * Update in-house vs outsourced on a BOQ operation from the work order (stage must be Pending or On Hold).
   */
  async updateBoqOperationOutsource(
    workOrderId: string,
    operationId: string,
    body: {
      isOutsourced: boolean;
      outsourceCost?: number | null;
      outsourcedSupplierCategoryIds?: string[];
      outsourcedScopeDetail?: string | null;
      notes?: string;
    }
  ): Promise<void> {
    const response = await api.put(
      `/manufacturing/work-orders/${workOrderId}/boq-operations/${operationId}/outsource`,
      body
    );
    if ((response as { success?: boolean })?.success === false) {
      throw new Error((response as { message?: string })?.message || 'Update failed');
    }
  }

  /** Prefill for Inland Process Deviation (in-house → outsourced). */
  /** Company-wide list for Sales / reporting (optional status filter). */
  async listInlandProcessDeviations(params?: { status?: string }): Promise<
    Array<{
      id: string;
      deviation_number: string;
      work_order_id: string;
      work_order_number?: string | null;
      status: string;
      justification_reason?: string | null;
      created_at?: string;
      generated_purchase_requisition_id?: string | null;
      pr_number?: string | null;
    }>
  > {
    const qs = new URLSearchParams();
    if (params?.status?.trim()) qs.set('status', params.status.trim());
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const res = await api.get(`/manufacturing/inland-process-deviations${suffix}`);
    const rows = (res as { data?: unknown }).data ?? res;
    if (!Array.isArray(rows)) return [];
    return rows as Array<{
      id: string;
      deviation_number: string;
      work_order_id: string;
      work_order_number?: string | null;
      status: string;
      justification_reason?: string | null;
      created_at?: string;
      generated_purchase_requisition_id?: string | null;
      pr_number?: string | null;
    }>;
  }

  async getInlandDeviationPrefill(
    workOrderId: string,
    operationId: string
  ): Promise<InlandDeviationPrefillResponse> {
    const response = await api.get(
      `/manufacturing/work-orders/${workOrderId}/boq-operations/${operationId}/inland-deviation-prefill`
    );
    const raw = response.data as { data?: unknown; message?: string };
    const d = raw?.data ?? raw;
    if (!d || typeof d !== 'object') throw new Error(raw?.message || 'Failed to load prefill');
    return d as InlandDeviationPrefillResponse;
  }

  async createInlandProcessDeviation(
    workOrderId: string,
    operationId: string,
    body: {
      proposed_outsource_cost?: number | null;
      proposed_supplier_category_ids?: string[];
      justification_reason: string;
      justification_category?: string;
      pr_payload: {
        pr_date: string;
        required_date: string;
        department_id: string;
        notes?: string;
        lines: Array<{
          item_id: string;
          requested_quantity: number;
          uom: string;
          estimated_unit_price?: number;
        }>;
      };
    }
  ): Promise<{ id: string; deviation_number?: string; status?: string }> {
    const response = await api.post(
      `/manufacturing/work-orders/${workOrderId}/boq-operations/${operationId}/inland-process-deviations`,
      body
    );
    const raw = response.data as { data?: unknown };
    const row = raw?.data ?? raw;
    return row as { id: string; deviation_number?: string; status?: string };
  }

  async submitInlandProcessDeviation(deviationId: string): Promise<void> {
    await api.post(`/manufacturing/inland-process-deviations/${deviationId}/submit`);
  }

  async getInlandProcessDeviation(
    deviationId: string
  ): Promise<Record<string, unknown> & { id: string; deviation_number?: string; status?: string }> {
    const response = await api.get(`/manufacturing/inland-process-deviations/${deviationId}`);
    const raw = response.data as { data?: unknown };
    return (raw?.data ?? raw) as Record<string, unknown> & { id: string };
  }

  async getRouteSheetData(workOrderId: string, baseUrl?: string): Promise<RouteSheetPrintData> {
    const response = await api.get<RouteSheetPrintData>(
      `/manufacturing/work-orders/${workOrderId}/route-sheet-data`,
      { params: baseUrl ? { base_url: baseUrl } : undefined }
    );
    if (response?.data) return response.data;
    throw new Error(response?.message || 'Failed to load route sheet');
  }

  async generateRouteSheet(
    workOrderId: string,
    body?: { reason?: string; base_url?: string }
  ): Promise<RouteSheetPrintData> {
    const response = await api.post<RouteSheetPrintData>(
      `/manufacturing/work-orders/${workOrderId}/route-sheet/generate`,
      body ?? {}
    );
    if (response?.data) return response.data;
    throw new Error(response?.message || 'Failed to generate route sheet');
  }

  async getRouteSheetRegister(filters?: RouteSheetRegisterFilters): Promise<RouteSheetRegisterRow[]> {
    const response = await api.get<RouteSheetRegisterRow[]>('/manufacturing/route-sheets/register', {
      params: filters,
    });
    return response.data ?? [];
  }
}

export interface RouteSheetStageRow {
  stage_id: string;
  operation_no: number;
  /** Parent BOQ operation description (routing step). */
  operation_name: string;
  /** Shop-floor stage name when present. */
  stage_name?: string | null;
  stage_code?: string | null;
  work_center: string | null;
  planned_hours: number | null;
  operator_name: string | null;
  start_time: string | null;
  end_time: string | null;
  status: string;
  remarks: string | null;
  inspection_required: boolean;
  inspection_status: string | null;
  inspector_name: string | null;
  is_outsourced: boolean;
  outsourced_pr_id?: string | null;
  outsource_vendor?: string | null;
  outsource_po_number?: string | null;
  outsource_grn_status?: string | null;
  production_unit_number: number | null;
  quantity: number | null;
}

export interface RouteSheetPrintData {
  header: {
    route_sheet_no: string;
    revision_no: number;
    work_order_id: string;
    work_order_number: string;
    sales_order_number: string | null;
    production_request_number: string | null;
    customer_name: string | null;
    project_name: string | null;
    execution_scope: string | null;
    item_name: string | null;
    item_code: string | null;
    /** Item / BOQ drawing revision (not route-sheet revision_no). */
    item_revision?: string | null;
    boq_line_no: string | null;
    drawing_no: string | null;
    quantity: number;
    uom: string;
    planned_start_date: string | null;
    required_delivery_date: string | null;
    prepared_by: string | null;
    print_datetime: string;
    work_order_updated_at: string | null;
    needs_regeneration: boolean;
    regeneration_reason: string | null;
    has_generation: boolean;
    client_scope_of_work?: string | null;
    client_quality_requirements?: string | null;
    wo_customer_requirements_notes?: string | null;
    /** Work order deviation type (RFD / Email / N/A). */
    deviation_type?: string | null;
  };
  layout_mode: 'unit_wise' | 'boq_line';
  fabrication_units: Array<{
    unit_number: number | 'legacy';
    unit_label: string;
    operations: RouteSheetStageRow[];
  }>;
  boq_lines: Array<{
    work_order_line_item_id: string;
    line_number: number;
    boq_line_no: string;
    item_code?: string | null;
    item_description: string;
    drawing_no: string | null;
    quantity: number;
    uom: string;
    revision: string | null;
    operations: RouteSheetStageRow[];
    material_entries?: Array<{
      drawing_line_no: string | null;
      client_material_specification: string | null;
      material_grade: string | null;
      heat_no: string | null;
      verified?: boolean;
      not_verified?: boolean;
      remarks?: string | null;
    }>;
  }>;
  inspection_summary: Array<{
    stage_id: string;
    operation_name: string;
    unit_label: string | null;
    inspection_required: boolean;
    inspection_type: string;
    inspection_status: string | null;
    inspector_name: string | null;
    inspection_date: string | null;
    remarks: string | null;
  }>;
  materials: Array<{
    line_number: number;
    material: string | null;
    qty: string | null;
    requested_qty?: string | null;
    issued_qty?: string | null;
    grade: string | null;
    heat_number: string | null;
    mtc_reference: string | null;
    remarks: string | null;
    work_order_line_item_id?: string | null;
    boq_line_item_id?: string | null;
    boq_line_label?: string | null;
    request_number?: string | null;
  }>;
  material_utilization?: Array<{
    line_number: number;
    work_order_line_item_id?: string | null;
    drawing_line_no: string | null;
    client_material_specification: string | null;
    material_grade: string | null;
    head_no: string | null;
    verified: boolean;
    not_verified: boolean;
    remarks: string | null;
  }>;
  traceability: {
    heat_numbers: string[];
    lot_numbers: string[];
    serial_numbers: string[];
    tag_numbers: string[];
    matrix_reference: string | null;
  };
  outsource_operations: Array<{
    operation_name: string;
    vendor: string | null;
    po_number: string | null;
    pr_number: string | null;
    grn_status: string | null;
    inspection_required: boolean;
  }>;
  qr_url: string;
  qr_payload: string;
}

export interface RouteSheetRegisterFilters {
  work_order_id?: string;
  customer_name?: string;
  project_name?: string;
  execution_scope?: string;
  from_date?: string;
  to_date?: string;
}

export interface RouteSheetRegisterRow {
  id: string;
  work_order_id: string;
  revision_no: number;
  route_sheet_no: string;
  reason: string | null;
  generated_by: string | null;
  generated_at: string;
  work_order_number: string;
  execution_scope: string | null;
  project_name: string | null;
  customer_name: string | null;
}

const workOrderService = new WorkOrderService();
export default workOrderService;
export { workOrderService };
